import {
  attendanceApi,
  auditLogApi,
  auditLogConfigApi,
  blockedDateApi,
  breakTimeSettingsApi,
  dashboardApi,
  employeeApi,
  employeeLeaveBalanceApi,
  employeeLicenseApi,
  employeeReportTemplatesApi,
  faceApi,
  holidayAdminApi,
  holidayRequestApi,
  incidentApi,
  jobApplicantApi,
  jobMonitorApi,
  jobsApi,
  leaveLedgerApi,
  leavePoliciesApi,
  leaveTypeApi,
  messageApi,
  notificationApi,
  payModelApi,
  payRecordApi,
  payrollBuilderReportTemplatesApi,
  payrollDepartmentApi,
  payrollRunApi,
  planVsActualReportApi,
  publicHolidayApi,
  publicHolidayPolicyApi,
  scheduleShiftImportApi,
  schedulingApi,
  securityCommandControlLogApi,
  shiftApi,
  shiftGroupApi,
  shiftMarketplaceApi,
  shiftTemplateApi,
  systemSettingsApi,
  taskApi,
  timeStationApi,
  timecardApi,
  trainingRecordApi,
  trainingTypeApi,
  workingDayApi,
  earlyLateReportApi,
  employeeDetailsReportRunsApi,
  invoiceReportRunsApi,
  payrollDetailsReportRunsApi,
} from '../services/api';
import type { QaCheckRow, QaResultStatus, QaTimecardDefaults } from './timecardScenarios/types';

export type ModuleCheckGroup =
  | 'Dashboard'
  | 'Employees'
  | 'Scheduling'
  | 'Attendance'
  | 'Timecards'
  | 'Reports'
  | 'Payroll'
  | 'Leave'
  | 'Incidents'
  | 'Tasks'
  | 'Messages'
  | 'Marketplace'
  | 'Training'
  | 'Jobs'
  | 'Settings';

type ModuleDef = {
  id: string;
  title: string;
  group: ModuleCheckGroup;
  run: (defaults: QaTimecardDefaults) => Promise<{ status: QaResultStatus; message: string }>;
};

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function addDays(dateStr: string, days: number) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function weekRange(defaults: QaTimecardDefaults) {
  const start = defaults.sandboxWeekStart || defaults.weekStart;
  return { start, end: addDays(start, 6) };
}

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

function failOrSkip(err: unknown): { status: QaResultStatus; message: string } {
  const status = httpStatus(err);
  const message = err instanceof Error ? err.message : String(err);
  if (status === 401 || status === 403) {
    return { status: 'skipped', message: `No permission for this module (${status})` };
  }
  if (status === 404) {
    return { status: 'skipped', message: 'Not available on this tenant (404)' };
  }
  return { status: 'failed', message };
}

function listed(label: string, rows: unknown) {
  const list = Array.isArray(rows) ? rows : [];
  return { status: 'passed' as const, message: `${label}: ${list.length} record(s)` };
}

function loaded(label: string, value: unknown) {
  if (value == null) {
    return { status: 'failed' as const, message: `${label} returned empty` };
  }
  return { status: 'passed' as const, message: `${label} loaded` };
}

export const MODULE_CHECK_DEFS: ModuleDef[] = [
  {
    id: 'MOD-DASH-001',
    title: 'Dashboard summary loads',
    group: 'Dashboard',
    run: async (defaults) => loaded('Dashboard summary', await dashboardApi.getSummary(defaults.locationId)),
  },
  {
    id: 'MOD-DASH-002',
    title: 'Dashboard alerts data loads',
    group: 'Dashboard',
    run: async () => loaded('Dashboard alerts', await dashboardApi.getAlertsData()),
  },
  {
    id: 'MOD-EMP-001',
    title: 'Sandbox employee profile loads',
    group: 'Employees',
    run: async (defaults) => {
      const emp = await employeeApi.getById(defaults.employeeId);
      const name = [emp.firstName, emp.lastName].filter(Boolean).join(' ').trim() || emp.email;
      return { status: 'passed', message: `Loaded ${name} (id ${emp.id})` };
    },
  },
  {
    id: 'MOD-EMP-002',
    title: 'Organization hierarchy loads',
    group: 'Employees',
    run: async (defaults) => loaded('Organization hierarchy', await employeeApi.getOrganizationHierarchy(defaults.locationId)),
  },
  {
    id: 'MOD-SCH-001',
    title: 'Scheduling bootstrap loads',
    group: 'Scheduling',
    run: async () => loaded('Scheduling bootstrap', await schedulingApi.getBootstrap()),
  },
  {
    id: 'MOD-SCH-002',
    title: 'Scheduling week view loads',
    group: 'Scheduling',
    run: async (defaults) => {
      const { start, end } = weekRange(defaults);
      if (defaults.scheduleId == null) {
        return { status: 'skipped', message: 'No sandbox schedule id' };
      }
      return loaded(
        'Week view',
        await schedulingApi.getWeekView({
          locationId: defaults.locationId,
          scheduleId: defaults.scheduleId,
          weekStart: start,
          weekEnd: end,
        })
      );
    },
  },
  {
    id: 'MOD-SCH-003',
    title: 'Shift templates list loads',
    group: 'Scheduling',
    run: async (defaults) => listed('Shift templates', await shiftTemplateApi.getAll(defaults.locationId)),
  },
  {
    id: 'MOD-SCH-004',
    title: 'Sandbox week shifts list loads',
    group: 'Scheduling',
    run: async (defaults) => {
      const { start, end } = weekRange(defaults);
      return listed(
        'Shifts',
        await shiftApi.getAll({
          employeeId: defaults.employeeId,
          locationId: defaults.locationId,
          startDate: start,
          endDate: end,
        })
      );
    },
  },
  {
    id: 'MOD-SCH-005',
    title: 'Shift groups list loads',
    group: 'Scheduling',
    run: async (defaults) => listed('Shift groups', await shiftGroupApi.getAll({ locationId: defaults.locationId })),
  },
  {
    id: 'MOD-SCH-006',
    title: 'Schedule import history loads',
    group: 'Scheduling',
    run: async (defaults) => {
      if (defaults.scheduleId == null) {
        return { status: 'skipped', message: 'No sandbox schedule id' };
      }
      const page = await scheduleShiftImportApi.importHistory(defaults.locationId, defaults.scheduleId, {
        page: 1,
        pageSize: 5,
      });
      return listed('Import batches', page.items);
    },
  },
  {
    id: 'MOD-ATT-001',
    title: 'Sandbox week attendance list loads',
    group: 'Attendance',
    run: async (defaults) => {
      const { start, end } = weekRange(defaults);
      return listed(
        'Attendance',
        await attendanceApi.getAll({
          employeeId: defaults.employeeId,
          locationId: defaults.locationId,
          startDate: start,
          endDate: end,
        })
      );
    },
  },
  {
    id: 'MOD-ATT-002',
    title: 'Time stations list loads',
    group: 'Attendance',
    run: async () => listed('Time stations', await timeStationApi.getAll()),
  },
  {
    id: 'MOD-ATT-003',
    title: 'Attendance import history loads',
    group: 'Attendance',
    run: async (defaults) => {
      const page = await attendanceApi.importHistory(defaults.locationId, { page: 1, pageSize: 5 });
      return listed('Import batches', page.items);
    },
  },
  {
    id: 'MOD-ATT-004',
    title: 'Face sync status loads',
    group: 'Attendance',
    run: async () => {
      const status = await faceApi.getSyncStatus();
      return {
        status: 'passed',
        message: `Face sync loaded (db=${status.databaseFaceCount}, rekognition=${status.rekognitionFaceCount})`,
      };
    },
  },
  {
    id: 'MOD-TC-001',
    title: 'Timecards list loads for sandbox week',
    group: 'Timecards',
    run: async (defaults) => {
      const { start } = weekRange(defaults);
      return listed('Timecards', await timecardApi.getAll(defaults.employeeId, start, defaults.locationId));
    },
  },
  {
    id: 'MOD-REP-001',
    title: 'Plan vs actual week report loads',
    group: 'Reports',
    run: async (defaults) => {
      const { start, end } = weekRange(defaults);
      return loaded(
        'Plan vs actual',
        await planVsActualReportApi.getWeekReport({
          weekStartDate: start,
          weekStartDisplay: start,
          weekEndDisplay: end,
          locationId: defaults.locationId,
        })
      );
    },
  },
  {
    id: 'MOD-REP-002',
    title: 'Audit logs page loads',
    group: 'Reports',
    run: async () => {
      const page = await auditLogApi.getAll({ page: 1, pageSize: 10 });
      return {
        status: 'passed',
        message: `Audit logs: ${page?.data?.length ?? 0} of ${page?.totalCount ?? 0}`,
      };
    },
  },
  {
    id: 'MOD-REP-003',
    title: 'Leave ledger report loads',
    group: 'Reports',
    run: async (defaults) => {
      const { start, end } = weekRange(defaults);
      return listed('Leave ledger', await leaveLedgerApi.getReport(start, end));
    },
  },
  {
    id: 'MOD-REP-004',
    title: 'Employee report templates list loads',
    group: 'Reports',
    run: async () => listed('Employee report templates', await employeeReportTemplatesApi.list()),
  },
  {
    id: 'MOD-REP-005',
    title: 'Early/late report runs list loads',
    group: 'Reports',
    run: async () => listed('Early/late reports', await earlyLateReportApi.list(20)),
  },
  {
    id: 'MOD-REP-006',
    title: 'Audit log configs list loads',
    group: 'Reports',
    run: async () => listed('Audit log configs', await auditLogConfigApi.getAll()),
  },
  {
    id: 'MOD-REP-007',
    title: 'Employee details report runs list loads',
    group: 'Reports',
    run: async () => listed('Employee details reports', await employeeDetailsReportRunsApi.list(20)),
  },
  {
    id: 'MOD-REP-008',
    title: 'Invoice report runs list loads',
    group: 'Reports',
    run: async () => listed('Invoice reports', await invoiceReportRunsApi.list(20)),
  },
  {
    id: 'MOD-REP-009',
    title: 'Payroll details report runs list loads',
    group: 'Reports',
    run: async () => listed('Payroll details reports', await payrollDetailsReportRunsApi.list(20)),
  },
  {
    id: 'MOD-PAY-001',
    title: 'Payroll runs list loads',
    group: 'Payroll',
    run: async () => listed('Payroll runs', await payrollRunApi.list(20)),
  },
  {
    id: 'MOD-PAY-002',
    title: 'Payroll builder templates list loads',
    group: 'Payroll',
    run: async () => listed('Payroll builder templates', await payrollBuilderReportTemplatesApi.list()),
  },
  {
    id: 'MOD-PAY-003',
    title: 'Pay models list loads',
    group: 'Payroll',
    run: async () => listed('Pay models', await payModelApi.getAll()),
  },
  {
    id: 'MOD-PAY-004',
    title: 'Sandbox pay records list loads',
    group: 'Payroll',
    run: async (defaults) => listed('Pay records', await payRecordApi.getByEmployeeId(defaults.employeeId)),
  },
  {
    id: 'MOD-PAY-005',
    title: 'Payroll departments list loads',
    group: 'Payroll',
    run: async () => listed('Payroll departments', await payrollDepartmentApi.getAll()),
  },
  {
    id: 'MOD-LV-001',
    title: 'Leave types list loads',
    group: 'Leave',
    run: async () => listed('Leave types', await leaveTypeApi.getAll()),
  },
  {
    id: 'MOD-LV-002',
    title: 'Leave policies list loads',
    group: 'Leave',
    run: async () => listed('Leave policies', await leavePoliciesApi.getAll()),
  },
  {
    id: 'MOD-LV-003',
    title: 'Sandbox holiday requests list loads',
    group: 'Leave',
    run: async (defaults) => listed('Holiday requests', await holidayRequestApi.getAll(defaults.employeeId)),
  },
  {
    id: 'MOD-LV-004',
    title: 'Holiday admin bootstrap loads',
    group: 'Leave',
    run: async () => loaded('Holiday admin', await holidayAdminApi.getBootstrap()),
  },
  {
    id: 'MOD-LV-005',
    title: 'Public holidays list loads',
    group: 'Leave',
    run: async () => listed('Public holidays', await publicHolidayApi.getAll()),
  },
  {
    id: 'MOD-LV-006',
    title: 'Public holiday policies list loads',
    group: 'Leave',
    run: async () => listed('Holiday policies', await publicHolidayPolicyApi.getAll()),
  },
  {
    id: 'MOD-LV-007',
    title: 'Sandbox leave balances list loads',
    group: 'Leave',
    run: async (defaults) => listed('Leave balances', await employeeLeaveBalanceApi.getAll(defaults.employeeId)),
  },
  {
    id: 'MOD-LV-008',
    title: 'Blocked dates list loads',
    group: 'Leave',
    run: async () => listed('Blocked dates', await blockedDateApi.getAll()),
  },
  {
    id: 'MOD-INC-001',
    title: 'Sandbox incidents list loads',
    group: 'Incidents',
    run: async (defaults) =>
      listed('Incidents', await incidentApi.getAll({ employeeId: defaults.employeeId, locationId: defaults.locationId })),
  },
  {
    id: 'MOD-TASK-001',
    title: 'My tasks list loads',
    group: 'Tasks',
    run: async () => listed('My tasks', await taskApi.getMyTasks()),
  },
  {
    id: 'MOD-TASK-002',
    title: 'Task admin list loads',
    group: 'Tasks',
    run: async (defaults) => listed('Tasks', await taskApi.getAll({ locationId: defaults.locationId })),
  },
  {
    id: 'MOD-MSG-001',
    title: 'Message inbox loads',
    group: 'Messages',
    run: async (defaults) => listed('Inbox', await messageApi.getInbox({ locationId: defaults.locationId })),
  },
  {
    id: 'MOD-MSG-002',
    title: 'Unread message count loads',
    group: 'Messages',
    run: async (defaults) => {
      const data = await messageApi.getUnreadInboxCount(defaults.locationId);
      return { status: 'passed', message: `Unread count ${data.count}` };
    },
  },
  {
    id: 'MOD-MKT-001',
    title: 'Shift marketplace requests load',
    group: 'Marketplace',
    run: async (defaults) => listed('Marketplace requests', await shiftMarketplaceApi.getRequests({ locationId: defaults.locationId })),
  },
  {
    id: 'MOD-TRN-001',
    title: 'Training types list loads',
    group: 'Training',
    run: async () => listed('Training types', await trainingTypeApi.getAll()),
  },
  {
    id: 'MOD-TRN-002',
    title: 'Sandbox training records list loads',
    group: 'Training',
    run: async (defaults) => listed('Training records', await trainingRecordApi.getByEmployeeId(defaults.employeeId)),
  },
  {
    id: 'MOD-TRN-003',
    title: 'Sandbox compliance licenses list loads',
    group: 'Training',
    run: async (defaults) => listed('Licenses', await employeeLicenseApi.getByEmployeeId(defaults.employeeId)),
  },
  {
    id: 'MOD-JOB-001',
    title: 'Jobs list loads',
    group: 'Jobs',
    run: async () => listed('Jobs', await jobsApi.getAll()),
  },
  {
    id: 'MOD-JOB-002',
    title: 'Job applicants list loads',
    group: 'Jobs',
    run: async () => listed('Applicants', await jobApplicantApi.getAll()),
  },
  {
    id: 'MOD-JOB-003',
    title: 'Job monitor recurring jobs load',
    group: 'Jobs',
    run: async () => listed('Recurring jobs', await jobMonitorApi.getRecurringJobs()),
  },
  {
    id: 'MOD-SET-001',
    title: 'System settings list loads',
    group: 'Settings',
    run: async () => listed('System settings', await systemSettingsApi.getAll()),
  },
  {
    id: 'MOD-SET-002',
    title: 'Break settings load',
    group: 'Settings',
    run: async () => loaded('Break settings', await breakTimeSettingsApi.get()),
  },
  {
    id: 'MOD-SET-003',
    title: 'Working days list loads',
    group: 'Settings',
    run: async () => listed('Working days', await workingDayApi.getAll()),
  },
  {
    id: 'MOD-SET-004',
    title: 'Notifications list loads',
    group: 'Settings',
    run: async () => {
      const page = await notificationApi.getAll(1, 10);
      return {
        status: 'passed',
        message: `Notifications: ${page?.items?.length ?? 0} of ${page?.total ?? 0}`,
      };
    },
  },
  {
    id: 'MOD-SET-005',
    title: 'Security command and control log loads',
    group: 'Settings',
    run: async () => listed('Security log', await securityCommandControlLogApi.getAll()),
  },
];

export const MODULE_FILTER_ID = 'modules';

export function moduleCheckIds(): string[] {
  return MODULE_CHECK_DEFS.map((d) => d.id);
}

export function moduleRowsPending(filterGroup?: string): QaCheckRow[] {
  return MODULE_CHECK_DEFS.filter((d) => {
    if (!filterGroup || filterGroup === 'all' || filterGroup === MODULE_FILTER_ID) return true;
    return d.group.toLowerCase() === filterGroup.toLowerCase();
  }).map((d) => ({
    id: d.id,
    title: d.title,
    group: d.group,
    status: 'pending' as const,
    message: '',
  }));
}

export async function runModuleChecks(
  defaults: QaTimecardDefaults,
  onRow: (row: QaCheckRow) => void,
  filterGroup?: string
): Promise<void> {
  const defs = MODULE_CHECK_DEFS.filter((d) => {
    if (!filterGroup || filterGroup === 'all' || filterGroup === MODULE_FILTER_ID) return true;
    return d.group.toLowerCase() === filterGroup.toLowerCase();
  });

  for (const def of defs) {
    onRow({ id: def.id, title: def.title, group: def.group, status: 'running', message: '' });
    try {
      const result = await def.run(defaults);
      onRow({ id: def.id, title: def.title, group: def.group, status: result.status, message: result.message });
    } catch (err: unknown) {
      const result = failOrSkip(err);
      onRow({ id: def.id, title: def.title, group: def.group, status: result.status, message: result.message });
    }
  }
}
